// Pure, SSR-safe selection of ONE contextual supporting editorial image.
// Used identically by the client LandingPage and the parent SSR so the figure
// never shifts. Never blind-inserts: every asset needs authored context.
import { sanitizeSrcset } from "./landing-hero.mjs";

export const SUPPORTING_SIZES = "(min-width: 1024px) 720px, 100vw";
const BROAD = new Set(["wedding", "driving", "owner", "film"]);

/** Page purpose from category, page_type, filter_params and path. */
export function derivePageIntent(page) {
  const fp = page.filter_params || {};
  const t = [page.category, page.page_type, fp.rental_type, fp.occasion, page.full_path]
    .filter(Boolean).join(" ").toLowerCase();
  if (fp.make || fp.model || /marque|brand|american cars|vehicle-type|\bmake\b/.test(t)) return "make-model";
  if (/owner|list your car|list-a-car|onboarding/.test(t)) return "owner";
  if (/film|tv|photo/.test(t)) return "film";
  if (/wedding|bridal|bryllup/.test(t)) return "wedding";
  if (/self-drive|driving/.test(t)) return "driving";
  return "other";
}

function positiveInt(v) { const n = Number(v); return Number.isInteger(n) && n > 0 ? n : 0; }
const POS = /^(?:\d{1,3}(?:\.\d+)?%|left|right|center|top|bottom)(?: (?:\d{1,3}(?:\.\d+)?%|left|right|center|top|bottom))?$/;

/**
 * @returns {{ok:true, figure:{src,width,height,srcset,sizes,alt,objectPosition,placement}}|{ok:false, reason:string}}
 * placement: {kind:"after-h2", index:1} (after second h2 block) or {kind:"body-end"}.
 */
export function selectSupportingMedia({ page, heroUrl, bodyHtml }) {
  const assets = page.reviewed_supporting_assets;
  if (!Array.isArray(assets) || !assets.length) return { ok: false, reason: "no-supporting-assets" };
  const body = String(bodyHtml || "");
  if (/<(?:figure|img)\b/i.test(body)) return { ok: false, reason: "body-has-inline-figure" };
  const h2s = (body.match(/<h2\b/gi) || []).length;
  if (h2s < 1) return { ok: false, reason: "no-authored-body" };
  const intent = derivePageIntent(page);
  if (!BROAD.has(intent)) return { ok: false, reason: `unsupported-intent:${intent}` };
  const asset = assets[0];
  const ctx = asset.context;
  if (!asset || !asset.url) return { ok: false, reason: "no-url" };
  if (!ctx || ctx.approved !== true) return { ok: false, reason: "no-authored-context" };
  if (!Array.isArray(ctx.intents) || !ctx.intents.includes(intent)) return { ok: false, reason: "intent-mismatch" };
  if (ctx.make || ctx.model) return { ok: false, reason: "model-specific-asset" };
  if (asset.url === heroUrl) return { ok: false, reason: "same-as-hero" };
  if (typeof asset.alt !== "string" || !asset.alt.trim()) return { ok: false, reason: "no-native-alt" };
  const width = positiveInt(asset.width), height = positiveInt(asset.height);
  if (!width || !height) return { ok: false, reason: "no-dimensions" };
  const { srcset, candidates } = sanitizeSrcset(asset.srcset);
  if (!srcset) return { ok: false, reason: "no-safe-srcset" };
  if (!/^https:\/\//i.test(asset.url) || /unsplash/i.test(asset.url)) return { ok: false, reason: "unsafe-url" };
  const sorted = [...candidates].sort((a, b) => a.width - b.width);
  const src = (sorted.find((c) => c.width >= 1280) || sorted[sorted.length - 1]).url;
  return {
    ok: true,
    figure: {
      src, width, height, srcset, sizes: SUPPORTING_SIZES, alt: asset.alt.trim(),
      objectPosition: typeof asset.objectPosition === "string" && POS.test(asset.objectPosition.trim()) ? asset.objectPosition.trim() : "center",
      placement: h2s >= 2 ? { kind: "after-h2", index: 1 } : { kind: "body-end" },
    },
  };
}

const ENTITIES = { "&#39;": "'", "&quot;": '"', "&amp;": "&", "&lt;": "<", "&gt;": ">", "&nbsp;": " " };
const decode = (t) => t.replace(/&#39;|&quot;|&amp;|&lt;|&gt;|&nbsp;/g, (m) => ENTITIES[m]);

/**
 * Source bodies that are ONE `<p>` holding blank-line separated native text
 * (short heading lines, paragraphs, "•" bullet lines). Converts them to
 * h2/p/ul blocks WITHOUT rewriting any native words. Returns null when the
 * shape is not safe (no headings, nested markup, or unclear structure).
 */
export function parseWrappedPlainBody(html) {
  const m = String(html || "").trim().match(/^<p>([\s\S]*)<\/p>$/);
  if (!m || /<[a-z/][^>]*>/i.test(m[1])) return null;
  const parts = m[1].split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  if (parts.length < 3) return null;
  const blocks = [];
  parts.forEach((part, i) => {
    const lines = part.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.every((l) => /^[•\-*]\s+/.test(l))) {
      blocks.push({ type: "ul", items: lines.map((l) => decode(l.replace(/^[•\-*]\s+/, ""))) });
    } else if (lines.length === 1 && lines[0].length <= 90 && !/[.!?:;。]$/.test(lines[0]) && i < parts.length - 1) {
      blocks.push({ type: "h2", text: decode(lines[0]) });
    } else {
      blocks.push({ type: "p", text: decode(lines.join(" ")) });
    }
  });
  return blocks.some((b) => b.type === "h2") ? blocks : null;
}

/** Index to insert the supporting figure BEFORE (the third h2), or -1 for body end. */
export function supportingInsertIndex(blockTypes, placement) {
  if (!placement || placement.kind !== "after-h2") return -1;
  let seen = 0;
  for (let i = 0; i < blockTypes.length; i++) {
    if (blockTypes[i] === "h2" && ++seen === placement.index + 2) return i;
  }
  return -1;
}

/** Minimal markup the selector can count h2 blocks in (no native text). */
export function blocksToCountingHtml(blocks) {
  return blocks.map((b) => (b.type === "h2" ? "<h2></h2>" : "<p></p>")).join("");
}
